import React from 'react';
import { Head, Link } from '@inertiajs/react';
import AdminLayout from '../../../components/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Eye, Trash2 } from 'lucide-react';

interface PageView {
  id: number;
  physiotherapist: {
    id: number;
    first_name: string;
    last_name: string;
  };
  ip_address: string;
  user_agent: string;
  referrer: string;
  path: string;
  created_at: string;
  updated_at: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
}

interface Props {
  page_views: PageView[];
  pagination: Pagination;
}

const PageViewsIndex: React.FC<Props> = ({ page_views, pagination }) => {
  const truncateUserAgent = (userAgent: string) => {
    return userAgent.length > 40 ? userAgent.substring(0, 40) + '...' : userAgent;
  };

  const truncateReferrer = (referrer: string) => {
    return referrer.length > 30 ? referrer.substring(0, 30) + '...' : referrer;
  };

  return (
    <AdminLayout title="Gestion des page views">
      <Head title="Page Views - Admin" />
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Page Views</h1>
            <p className="text-gray-600">
              {pagination.total_count} page view{pagination.total_count > 1 ? 's' : ''} au total
            </p>
          </div>
        </div>

        {/* Page Views Table */}
        <Card>
          <CardHeader>
            <CardTitle>Liste des consultations de pages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-900">ID</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Kinésithérapeute</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Page</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">IP</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Référent</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Consultée le</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {page_views.map((pageView) => (
                    <tr key={pageView.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm text-gray-900">#{pageView.id}</td>
                      <td className="py-3 px-4 text-sm text-gray-900">
                        <Link 
                          href={`/admin/physiotherapists/${pageView.physiotherapist.id}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {pageView.physiotherapist.first_name} {pageView.physiotherapist.last_name}
                        </Link>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 font-mono">
                        {pageView.path}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {pageView.ip_address || 'Non spécifié'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        <span title={pageView.referrer}>
                          {pageView.referrer ? truncateReferrer(pageView.referrer) : 'Direct'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">
                        {new Date(pageView.created_at).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <Link href={`/admin/page_views/${pageView.id}`}>
                            <Button size="sm" variant="ghost">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Link 
                            href={`/admin/page_views/${pageView.id}`} 
                            method="delete" 
                            as="button"
                            preserveScroll
                            onBefore={() => {
                              if (!confirm('Êtes-vous sûr de vouloir supprimer cette page view ?')) {
                                return false;
                              }
                            }}
                          >
                            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-800">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Page {pagination.current_page} sur {pagination.total_pages}
                </div>
                <div className="flex space-x-2">
                  {pagination.current_page > 1 && (
                    <Link href={`/admin/page_views?page=${pagination.current_page - 1}`}>
                      <Button variant="outline" size="sm">
                        Précédent
                      </Button>
                    </Link>
                  )}
                  {pagination.current_page < pagination.total_pages && (
                    <Link href={`/admin/page_views?page=${pagination.current_page + 1}`}>
                      <Button variant="outline" size="sm">
                        Suivant
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PageViewsIndex; 
